import eventBus from "@/src/utils/eventBus.js";

import { entityViews, references } from "@/src/constants/pageConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";

export const engagementType = {
  CALL: "Mongo::Call",
  NOTE: "Mongo::Note",
  MESSAGE: "Mongo::ConversationMessage",
  ACTION: "MySQL::Action",
  TASK: "MySQL::Task",
  CRM_TASK: "CRM::Task",
  MARKET_RESEARCH_STARTED: "MySQL:MarketResearchStarted",
  MARKET_RESEARCH_COMPLETED: "MySQL:MarketResearchCompleted",
  SMS: "Mongo::SmsLog",
  WHATSAPP: "Mongo::WhatsappLog",
  MEETING: "Mongo::Meeting",
  LINKEDIN: "Mongo::LinkedinLog",
  COMMENT: "Mongo::Comment",
  PHONE_CALL: "CallService::MySQL::PhoneCall",
  MYSQL_MEETING: "MeetingService::MySQL::Meeting",
  EMAILS: "Meetings::MySQL::Emails",
  FORM: "Meetings::MySQL::FormSubmitted",
  RESCHEDULED: "Meetings::MySQL::Rescheduled",
  CREATED: "Meetings::MySQL::Created",
  CANCELLED: "Meetings::MySQL::Cancelled",
  COMPLETED: "Meetings::MySQL::Completed",
  ZOOM_MEETING_SUMMARY: "Meetings::Zoom::SummaryGenerated",
  GOOGLE_MEET_SUMMARY: "Meetings::GoogleMeet::SummaryGenerated",
  GOOGLE_MEET_RECORDING: "Meetings::GoogleMeet::RecordingProcessed",
  WEBSITE_VISIT: "Mongo::WebsiteVisitLog",
  SIGNAL_ACTIVITY: "Mongo::SignalActivity",
  CAMPAIGN_EMAIL_ACTIVITY: "Mongo::MarketingCampaignActivity",
  WHATSAPP_ACTIVITY: "Mongo::WhatsappActivity",
  LIVECHAT_SESSION_CLOSED: "Livechat::Session::Closed",
  LIVECHAT_SESSION_REOPENED: "Livechat::Session::Reopened",
  LIVECHAT_PRIORITY_UPDATED: "Livechat::Session::PriorityUpdated",
  LIVECHAT_ASSIGNEE_UPDATED: "Livechat::Session::AssigneeUpdated",
  FORM_SUBMIT_LOG: "Mongo::FormSubmitLog",
  CONTACT: "Contact",
  COMPANY: "Company",
  DEAL: "Deal",
  LEAD: "Lead",
  PROJECT_TASK_CREATED: "Projects::TaskCreated",
  CHAT_GROUP: "Chat::GroupCreated",
};

export const engagementTypeMapping = {
  [engagementType.CONTACT]: { title: "Contact", description: "added by" },
  [engagementType.COMPANY]: { title: "Company", description: "added by" },
  [engagementType.DEAL]: { title: "Deal", description: "added by" },
  [engagementType.LEAD]: { title: "Lead", description: "added by" },
  [engagementType.CALL]: { title: "Call", description: "logged by" },
  [engagementType.NOTE]: { title: "Note", description: "added by" },
  [engagementType.MESSAGE]: { title: "Email", description: "sent by" },
  [engagementType.ACTION]: { title: "Action", description: "added by" },
  [engagementType.TASK]: { title: "Task", description: "added by" },
  [engagementType.CRM_TASK]: { title: "Task added", description: "by" },
  [engagementType.CAMPAIGN_EMAIL_ACTIVITY]: { title: "Campaign email", description: "sent by" },
  [engagementType.WHATSAPP_ACTIVITY]: { title: "WhatsApp Activity", description: "sent by" },
  [engagementType.MARKET_RESEARCH_STARTED]: {
    title: "Market Research Generation",
    description: "started by",
  },
  [engagementType.MARKET_RESEARCH_COMPLETED]: {
    title: "Market Research Generation",
    description: "completed",
    hide: true,
  },
  [engagementType.SMS]: { title: "SMS", description: "sent by" },
  [engagementType.WHATSAPP]: { title: "WhatsApp", description: "sent by" },
  [engagementType.MEETING]: { title: "Meeting", description: "added by" },
  [engagementType.LINKEDIN]: { title: "LinkedIn Log", description: "added by" },
  [engagementType.COMMENT]: { title: "Comment", description: "added by" },
  [engagementType.PHONE_CALL]: { title: "Call", description: "with" },
  [engagementType.MYSQL_MEETING]: {
    title: "Meeting",
    description: "added by",
  },
  [engagementType.EMAILS]: { title: "Email", description: "sent by" },
  [engagementType.FORM]: {
    title: "Routing Form",
    description: "logged",
  },
  [engagementType.CREATED]: { title: "Created", description: "by" },
  [engagementType.RESCHEDULED]: { title: "Rescheduled", description: "by" },
  [engagementType.CANCELLED]: { title: "Cancelled", description: "by" },
  [engagementType.COMPLETED]: { title: "Completed", description: "by" },
  [engagementType.ZOOM_MEETING_SUMMARY]: {
    title: "Meeting Summary",
    description: "added",
  },
  [engagementType.GOOGLE_MEET_SUMMARY]: {
    title: "Meeting Summary",
    description: "generated for",
  },
  [engagementType.GOOGLE_MEET_RECORDING]: {
    title: "Meeting Recording",
    description: "processed for",
  },
  [engagementType.WEBSITE_VISIT]: {
    title: "Website Visit",
    description: "recorded",
    hide: true,
  },
  [engagementType.SIGNAL_ACTIVITY]: {
    title: "Signal Activity",
    description: "recorded",
    hide: true,
  },
  [engagementType.LIVECHAT_SESSION_CLOSED]: {
    title: "LiveChat",
    description: "closed by",
  },
  [engagementType.LIVECHAT_SESSION_REOPENED]: {
    title: "LiveChat",
    description: "re-opened by",
  },
  [engagementType.LIVECHAT_PRIORITY_UPDATED]: {
    title: "LiveChat",
    description: "priority updated by",
  },
  [engagementType.LIVECHAT_ASSIGNEE_UPDATED]: {
    title: "LiveChat",
    description: "assignee updated by",
  },
  [engagementType.FORM_SUBMIT_LOG]: {
    title: "Form",
    description: "submitted by",
  },
  [engagementType.PROJECT_TASK_CREATED]: {
    title: "Task",
    description: "created by",
  },
  [engagementType.CHAT_GROUP]: {
    title: "Chat Group",
    description: "created by",
  },
};

export const linkedinFormTypes = {
  INVITE: "INVITE",
  MESSAGE: "MESSAGE",
  INMAIL: "INMAIL",
};

export const linkedinType = {
  [linkedinFormTypes.INVITE]: { text: "Send LinkedIn Invite" },
  [linkedinFormTypes.MESSAGE]: { text: "Send LinkedIn Message" },
  [linkedinFormTypes.INMAIL]: { text: "Send LinkedIn InMail" },
};

export const activities = [
  {
    key: "log_call",
    action: () => eventBus.emit("open-call-drawer"),
    icon: imageNames.CALL_GREY,
    text: "Log Call",
  },
  {
    key: "log_sms",
    action: () => eventBus.emit("open-sms-drawer"),
    icon: imageNames.SMS_GREY,
    text: "Log SMS",
  },
  {
    key: "log_whatsapp_message",
    action: () => eventBus.emit("open-whatsapp-drawer"),
    icon: imageNames.WHATSAPP_GREY,
    text: "Log WhatsApp Message",
  },
  {
    key: "log_linkedin_message",
    action: () => eventBus.emit("open-linkedin-drawer"),
    icon: imageNames.SUITCASE_LIGHT,
    text: "Log LinkedIn Message",
  },
  {
    key: "log_meeting",
    action: () => eventBus.emit("open-meeting-drawer"),
    icon: imageNames.MEETING_GREY,
    text: "Log Meeting",
  },
];

export const activitySource = {
  [entityViews.CONTACT_VIEW]: references.CONTACTS,
  [entityViews.COMPANY_VIEW]: references.COMPANIES,
  [entityViews.LEAD_VIEW]: references.LEADS,
  [entityViews.DEAL_VIEW]: references.DEALS,
  [entityViews.MEETING]: references.MEETING,
  [entityViews.CALL_LOG_VIEW]: references.CALL_LOG,
  [entityViews.CRM_TASKS_VIEW]: references.CRM_TASKS,
  [entityViews.TICKETS_VIEW]: references.TICKETS,
};

export const activitiesRequiringContent = [engagementType.NOTE];

export const activityFilterTypes = {
  ALL_ACTIVITY: "all_activity",
  ENGAGEMENT_ACTIVITY: "engagement_activity",
  PROPERTY_CHANGES: "property_changes",
};

export const activityFilterOptions = [
  { label: "All activity", value: activityFilterTypes.ALL_ACTIVITY },
  {
    label: "Engagement activity",
    value: activityFilterTypes.ENGAGEMENT_ACTIVITY,
  },
  { label: "Property updates", value: activityFilterTypes.PROPERTY_CHANGES },
];

export const engagementTypeGroups = {
  CALLS: [engagementType.CALL, engagementType.PHONE_CALL],
  MEETINGS: [
    engagementType.MEETING,
    engagementType.MYSQL_MEETING,
    engagementType.EMAILS,
    engagementType.FORM,
    engagementType.CREATED,
    engagementType.RESCHEDULED,
    engagementType.CANCELLED,
    engagementType.COMPLETED,
    engagementType.ZOOM_MEETING_SUMMARY,
    engagementType.GOOGLE_MEET_SUMMARY,
    engagementType.GOOGLE_MEET_RECORDING,
  ],
  LIVECHAT: [
    engagementType.LIVECHAT_SESSION_CLOSED,
    engagementType.LIVECHAT_SESSION_REOPENED,
    engagementType.LIVECHAT_PRIORITY_UPDATED,
    engagementType.LIVECHAT_ASSIGNEE_UPDATED,
  ],
  MARKET_RESEARCH: [
    engagementType.MARKET_RESEARCH_STARTED,
    engagementType.MARKET_RESEARCH_COMPLETED,
  ],
  WHATSAPP: [
    engagementType.WHATSAPP,
    engagementType.WHATSAPP_ACTIVITY,
  ],
};

export const activityTypeOptions = [
  { label: "Calls", value: "calls", types: engagementTypeGroups.CALLS },
  { label: "Notes", value: engagementType.NOTE, types: [engagementType.NOTE] },
  { label: "Comments", value: engagementType.COMMENT, types: [engagementType.COMMENT] },
  { label: "Emails", value: engagementType.MESSAGE, types: [engagementType.MESSAGE] },
  { label: "Tasks", value: engagementType.TASK, types: [engagementType.TASK] },
  { label: "CRM Tasks", value: engagementType.CRM_TASK, types: [engagementType.CRM_TASK] },
  { label: "SMS", value: engagementType.SMS, types: [engagementType.SMS] },
  { label: "WhatsApp", value: "whatsapp", types: engagementTypeGroups.WHATSAPP },
  { label: "LinkedIn", value: engagementType.LINKEDIN, types: [engagementType.LINKEDIN] },
  { label: "Meetings", value: "meetings", types: engagementTypeGroups.MEETINGS },
  { label: "Form Submissions", value: engagementType.FORM_SUBMIT_LOG, types: [engagementType.FORM_SUBMIT_LOG] },
  { label: "Market Research", value: "market_research", types: engagementTypeGroups.MARKET_RESEARCH },
  { label: "Website Visits", value: engagementType.WEBSITE_VISIT, types: [engagementType.WEBSITE_VISIT] },
  { label: "Signal Activity", value: engagementType.SIGNAL_ACTIVITY, types: [engagementType.SIGNAL_ACTIVITY] },
  { label: "Campaign Email", value: engagementType.CAMPAIGN_EMAIL_ACTIVITY, types: [engagementType.CAMPAIGN_EMAIL_ACTIVITY] },
  { label: "LiveChat", value: "livechat", types: engagementTypeGroups.LIVECHAT },
].sort((a, b) => a.label.localeCompare(b.label));

export const activityValues = {
  EMPTY: "(empty)",
};
